import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Settings as SettingsIcon, Save, Image as ImageIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { RANKS } from "@/lib/ranks";

const FIELDS = [
  { key: "initial_elo", label: "ELO mặc định người mới", type: "number" },
  { key: "k_factor", label: "Hệ số K ELO", type: "number" },
  { key: "coin_win_rank", label: "Coin thưởng khi thắng rank (BO1)", type: "number" },
  { key: "coin_streak_3", label: "Coin thưởng chuỗi 3", type: "number" },
  { key: "coin_streak_5", label: "Coin thưởng chuỗi 5", type: "number" },
  { key: "point_to_coin_ratio", label: "Tỷ lệ quy đổi (điểm bắn đền / 1 coin)", type: "number" },
  { key: "challenge_expire_minutes", label: "Thời gian hết hạn lời mời (phút)", type: "number" },
];

const BOOLS = [
  { key: "nickname_approval_required", label: "Yêu cầu admin duyệt nickname" },
  { key: "require_admin_confirm_session", label: "Yêu cầu admin xác nhận phiên bắn đền" },
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [pending, setPending] = useState([]);

  const load = () => {
    api.get("/settings").then(({ data }) => setS(data));
    api.get("/players?limit=500").then(({ data }) => {
      setPending(data.filter((p) => p.nickname_approved === false || p.avatar_approved === false));
    });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.patch("/admin/settings", s);
      toast.success("Đã lưu cài đặt");
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const approve = async (p) => {
    await api.post(`/admin/players/${p.id}/approve-nickname`, { nickname: p.nickname_pending || p.nickname });
    toast.success(`Đã duyệt ${p.nickname_pending || p.nickname}`);
    load();
  };

  if (!s) return <div className="p-8 text-white/40">Đang tải...</div>;

  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      <h1 className="font-display font-bold text-3xl mb-1 flex items-center gap-3"><SettingsIcon className="text-cyan-300" /> Cài đặt hệ thống</h1>
      <p className="text-white/40 text-sm mb-6">Cấu hình ELO, coin, quy đổi điểm bắn đền và duyệt nickname/avatar</p>

      <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-5 mb-5">
        <h2 className="font-display font-semibold mb-4">Cài đặt điểm</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/50">{f.label}</span>
              <input type={f.type} value={s[f.key]} onChange={(e) => setS({ ...s, [f.key]: Number(e.target.value) })} data-testid={`set-${f.key}`}
                className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
            </label>
          ))}
        </div>
        <div className="mt-4 space-y-2">
          {BOOLS.map((f) => (
            <label key={f.key} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!s[f.key]} onChange={(e) => setS({ ...s, [f.key]: e.target.checked })} data-testid={`set-${f.key}`} />
              <span className="text-sm">{f.label}</span>
            </label>
          ))}
        </div>
        <button onClick={save} data-testid="save-settings" className="mt-5 px-5 py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black flex items-center gap-2">
          <Save size={15} /> Lưu cài đặt
        </button>
      </div>

      <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-5 mb-5">
        <h2 className="font-display font-semibold mb-4">Duyệt nickname & avatar ({pending.length})</h2>
        {pending.length === 0 ? (
          <div className="text-white/40 text-sm py-4 text-center">Không có yêu cầu chờ duyệt</div>
        ) : (
          <div className="space-y-2">
            {pending.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded bg-white/[0.02] border border-white/5" data-testid={`pending-approve-${p.id}`}>
                <img src={p.avatar_url} alt="" className="w-9 h-9 rounded-full" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">
                    {p.nickname_pending && p.nickname_pending !== p.nickname ? (
                      <>Tên cũ: <span className="text-white/50">{p.nickname}</span> → <span className="text-cyan-300 font-semibold">{p.nickname_pending}</span></>
                    ) : (
                      <>{p.nickname}</>
                    )}
                  </div>
                  <div className="text-[10px] text-white/40">
                    {p.avatar_approved === false && <span className="inline-flex items-center gap-1"><ImageIcon size={9} /> Avatar mới</span>}
                  </div>
                </div>
                <button onClick={() => approve(p)} data-testid={`approve-${p.id}`} className="px-3 py-1.5 rounded text-xs bg-cyan-400/15 text-cyan-300 border border-cyan-400/30 flex items-center gap-1">
                  <Check size={11} /> Duyệt
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-5">
        <h2 className="font-display font-semibold mb-4">Hệ thống Rank</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {RANKS.map((r) => (
            <div key={r.name} className={`rounded-md bg-gradient-to-br ${r.bg} border ${r.border} p-3`}>
              <div className="font-display font-bold" style={{ color: r.color }}>{r.name}</div>
              <div className="text-xs text-white/60 mt-1">{r.min} - {r.max === 99999 ? "∞" : r.max} ELO</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
